import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { finalize, tap, timeout } from 'rxjs/operators';
import { UserService } from '@app/core/services/UserService';
import { calculateSalaryAmounts } from './salary-calculation';

@Component({
  standalone: false,
  selector: 'salary-calculator-dialog',
  templateUrl: './salary-calculator-dialog.component.html',
  styleUrls: ['./salary-calculator-dialog.component.scss']
})
export class SalaryCalculatorDialogComponent implements OnInit {
  mode = 'gross';
  salaryType = 'monthly';
  amount: number = null;
  hours: number = null;
  amountDisplay = '';
  hoursDisplay = '';
  vacationMode = 'added';
  pensionTaxRate = 24.26;
  pensionPercent = 0;
  pensionMode = 'add';
  pensionCompanyId: number = null;
  pensionCompanySelectionVisible = false;
  includeCalculationDescription = false;
  pensionCompanies: any[] = [];
  calculations: any[] = [];
  private taxResults: any[] = [];
  private initialHourlyInvoiceAmount: number = null;
  private userProfiles: { [userId: string]: any } = {};
  private requestedUserProfiles: { [userId: string]: boolean } = {};
  errorMessage = '';
  calculating = false;

  constructor(
    public dialogRef: MatDialogRef<SalaryCalculatorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userService.getPensionSavingsCompanies().subscribe(companies => this.pensionCompanies = companies || []);
    this.loadUserProfiles();
    this.initializeFromInvoiceGroup();
    this.syncInputDisplays();
    if (this.hasValidInput() || this.initialHourlyInvoiceAmount != null) {
      this.calculate();
    }
  }

  private initializeFromInvoiceGroup(): void {
    const group = this.data?.group;
    if (!group) { return; }

    const savedCalculation = group.SalaryCalculator;
    const savedPensionPercent = Number(savedCalculation?.pensionPercent);
    this.pensionCompanyId = savedCalculation?.pensionCompanyId || null;
    const pensionPercent = Number(group.PensionSavingsPercent || 0);
    const pensionFactorPercent = Number(group.PensionSavingsFactor || 0) * 100;
    this.pensionPercent = isFinite(savedPensionPercent) && savedPensionPercent >= 0
      ? savedPensionPercent
      : (pensionPercent > 0 ? pensionPercent : pensionFactorPercent);
    this.pensionMode = group.PensionAddedToInvoice === false ? 'deduct' : 'add';

    if (['gross', 'invoice'].includes(savedCalculation?.mode) && Number(savedCalculation.amount) > 0) {
      this.mode = savedCalculation.mode;
      this.salaryType = savedCalculation.salaryType || 'monthly';
      this.amount = Number(savedCalculation.amount);
      this.hours = this.salaryType === 'hourly' ? Number(savedCalculation.hours || 0) : this.hours;
      this.vacationMode = savedCalculation.vacationMode || 'added';
      this.pensionMode = savedCalculation.pensionMode || 'add';
      return;
    }

    // A group amount is an explicitly entered payment amount. Do not infer a
    // new hourly calculation from its time rows; not all worked time must be
    // included in the amount.
    if (Number(group.Amount) > 0) {
      this.mode = 'invoice';
      this.salaryType = 'monthly';
      this.amount = Number(group.Amount);
      this.vacationMode = savedCalculation?.vacationMode || 'added';
      this.pensionMode = savedCalculation?.pensionMode || 'add';
      return;
    }

    // A previously entered hourly rate and number of hours are calculator
    // settings, not dated invoice rows. Keep them intact when this group is
    // opened for the first time.
    if (savedCalculation?.salaryType === 'hourly' && Number(savedCalculation.amount) > 0 && Number(savedCalculation.hours) > 0) {
      this.mode = savedCalculation.mode || 'gross';
      this.salaryType = 'hourly';
      this.amount = Number(savedCalculation.amount);
      this.hours = Number(savedCalculation.hours);
      this.vacationMode = savedCalculation.vacationMode || 'added';
      this.pensionMode = savedCalculation.pensionMode || 'add';
      return;
    }

    const invoiceRows = Array.isArray(group.InvoiceRows) ? group.InvoiceRows : [];
    const totalHours = invoiceRows.reduce((total, row) =>
      total + Number(row?.HoursWorked || 0) + (Number(row?.MinutesWorked || 0) / 60), 0);

    if (totalHours > 0) {
      this.salaryType = 'hourly';
      this.hours = totalHours;
      if (savedCalculation?.salaryType === 'hourly' && Number(savedCalculation.amount) > 0) {
        this.mode = savedCalculation.mode || 'gross';
        this.amount = Number(savedCalculation.amount);
        this.vacationMode = savedCalculation.vacationMode || 'added';
        this.pensionMode = savedCalculation.pensionMode || 'add';
        return;
      }
      this.initialHourlyInvoiceAmount = Number(group.Amount || 0);
      this.amount = null;
      this.vacationMode = 'added';
      return;
    }

    this.mode = 'invoice';
    this.salaryType = 'monthly';
    this.amount = Number(group.Amount || 0) || null;
    this.pensionMode = 'add';

  }

  get recipients(): any[] {
    const selectedRecipients = this.data?.recipients;
    if (Array.isArray(selectedRecipients) && selectedRecipients.length > 0) {
      return selectedRecipients;
    }

    const groupRecipient = this.data?.group?.PaymentReciever;
    return groupRecipient ? [groupRecipient] : [];
  }

  calculate(): void {
    this.errorMessage = '';
    const isWaitingForInitialHourlyRate = this.initialHourlyInvoiceAmount != null && this.salaryType === 'hourly' && this.hours > 0;
    if (!this.hasValidInput() && !isWaitingForInitialHourlyRate) {
      this.errorMessage = 'Ange ett positivt belopp och antal timmar för timlön.';
      return;
    }
    if (this.pensionPercent < 0 || this.pensionPercent > 35) {
      this.errorMessage = 'Pensionsavsättningen får vara högst 35 % av bruttolönen.';
      return;
    }

    if (this.recipients.length === 0) {
      this.errorMessage = 'Kunde inte hitta någon lönemottagare för beräkningen.';
      return;
    }

    const baseAmount = this.salaryType === 'hourly' ? this.amount * this.hours : this.amount;
    if (this.taxResults.length === this.recipients.length) {
      this.calculations = this.taxResults.map((result: any, index: number) => this.createCalculation(this.recipients[index], result, baseAmount));
      if (this.calculations.some(result => Object.keys(result).some(key =>
        typeof result[key] === 'number' && !isFinite(result[key])))) {
        this.calculations = [];
        this.errorMessage = 'Beloppet eller antalet timmar är för stort för att beräkna. Ange ett mindre värde.';
        return;
      }
      this.updatePensionCompanySelectionVisibility();
      return;
    }

    if (this.calculating) {
      return;
    }

    this.calculating = true;
    const requests = this.recipients.map(recipient => forkJoin({
      employerTax: this.userService.getUserEmployerTax(recipient.Id).pipe(
        tap(employerTax => {
          this.initializeHourlyGrossAmount([{ employerTax }]);
          this.syncInputDisplays();
          this.changeDetectorRef.detectChanges();
        }),
        timeout(5000)
      )
    }));

    forkJoin(requests).pipe(
      finalize(() => {
        this.calculating = false;
        this.changeDetectorRef.detectChanges();
      })
    ).subscribe(results => {
      this.taxResults = results;
      this.initializeHourlyGrossAmount(results);
      this.calculating = false;
      this.calculate();
      this.changeDetectorRef.detectChanges();
    }, () => {
      this.taxResults = [];
      this.calculations = [];
      this.errorMessage = 'Kunde inte hämta mottagarens skatteuppgifter. Förhandsvisningen visas när korrekta skattesatser har hämtats.';
    });
  }

  onCalculationValueChanged(): void {
    if (this.pensionPercent <= 0 || this.pensionMode !== 'add') {
      this.pensionCompanySelectionVisible = false;
    } else if (this.calculations.length) {
      this.updatePensionCompanySelectionVisibility();
    }
    if (this.hasValidInput()) {
      this.calculate();
    } else {
      this.calculations = [];
      this.errorMessage = 'Ange ett positivt belopp och antal timmar för timlön.';
    }
  }

  onAmountInput(value: string): void {
    this.amountDisplay = value;
    this.amount = this.parseInputValue(value);
    this.onCalculationValueChanged();
  }

  onHoursInput(value: string): void {
    this.hoursDisplay = value;
    this.hours = this.parseInputValue(value);
    this.onCalculationValueChanged();
  }

  formatAmountInput(): void {
    this.amountDisplay = this.formatInputValue(this.amount);
  }

  formatHoursInput(): void {
    this.hoursDisplay = this.formatInputValue(this.hours);
  }

  onPensionPercentChanged(value: number, input?: HTMLInputElement): void {
    const numericValue = Number(value);
    this.pensionPercent = isFinite(numericValue) ? Math.max(0, Math.min(35, numericValue)) : 0;
    if (input && numericValue !== this.pensionPercent) {
      input.value = String(this.pensionPercent);
    }
    this.onCalculationValueChanged();
  }

  private hasValidInput(): boolean {
    return isFinite(this.amount) && this.amount > 0 && (this.salaryType !== 'hourly'
      || (isFinite(this.hours) && this.hours > 0 && isFinite(this.amount * this.hours)));
  }

  private createCalculation(recipient: any, result: any, inputAmount: number): any {
    const employerTaxRate = Number(result.employerTax?.Tax || result.employerTax?.tax || 0);
    const municipalityTaxRate = Number(result.municipalityTax || 0);
    const pensionRate = Number(this.pensionPercent || 0) / 100;
    const feeRate = this.getServiceFeeRate();
    const isSelfEmployed = this.data?.employmentTypeId === 2;
    const { invoiceAmount, gross, grossWithVacation, vacationAmount, pension, pensionTotal, employerTax } =
      calculateSalaryAmounts(inputAmount, this.mode, this.vacationMode, this.pensionMode,
        pensionRate, employerTaxRate, isSelfEmployed ? feeRate : 0);

    const municipalityTax = gross * municipalityTaxRate;
    return {
      recipient,
      grossSalary: this.round(gross),
      grossSalaryWithVacation: this.round(grossWithVacation),
      netSalary: this.round(gross - municipalityTax),
      municipalityTax: this.round(municipalityTax),
      municipalityTaxRate,
      employerTax: this.round(employerTax),
      employerTaxRate,
      vacationAmount: this.round(vacationAmount),
      pensionAmount: this.round(pension),
      pensionTotalAmount: this.round(pensionTotal),
      serviceFee: Math.round(invoiceAmount * feeRate),
      serviceFeeRate: feeRate,
      invoiceAmount: this.round(invoiceAmount),
      user: this.userProfiles[String(recipient.Id)] || null
    };
  }

  private initializeHourlyGrossAmount(results: any[]): void {
    if (this.initialHourlyInvoiceAmount == null || !this.hours || !results.length) { return; }

    const employerTaxRate = Number(results[0]?.employerTax?.Tax || results[0]?.employerTax?.tax || 0);
    const feeRate = this.getServiceFeeRate();
    const availableAmount = this.initialHourlyInvoiceAmount - Math.round(this.initialHourlyInvoiceAmount * feeRate);
    const pensionRate = Number(this.pensionPercent || 0) / 100;
    // The existing invoice amount always contains the salary, vacation pay,
    // employer contribution and pension cost. The selected modes only decide
    // whether vacation pay/pension are included in the entered gross amount.
    const grossSalary = availableAmount /
      (1.12 * (1 + employerTaxRate + (pensionRate * 1.2426)));

    this.amount = grossSalary / this.hours;
    this.initialHourlyInvoiceAmount = null;
    this.syncInputDisplays();
  }

  private loadUserProfiles(): void {
    this.recipients.forEach(recipient => {
      const userId = String(recipient?.Id || '');
      if (!userId || this.requestedUserProfiles[userId]) { return; }

      this.requestedUserProfiles[userId] = true;
      this.userService.getUser(recipient.Id).subscribe(user => {
      this.userProfiles[userId] = user;
        if (this.taxResults.length === this.recipients.length && this.hasValidInput()) {
          this.calculate();
        }
      });
    });
  }

  private getServiceFeeRate(): number {
    return this.data?.employmentTypeId === 2 ? Number(this.data?.serviceFeeFactor || 0) : 0;
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private syncInputDisplays(): void {
    this.amountDisplay = this.formatInputValue(this.amount);
    this.hoursDisplay = this.formatInputValue(this.hours);
  }

  private formatInputValue(value: number): string {
    if (value == null || !isFinite(value)) { return ''; }
    const rounded = this.round(value);
    return isFinite(rounded) ? rounded.toFixed(2).replace('.', ',') : String(value).replace('.', ',');
  }

  private parseInputValue(value: string): number {
    const parsed = Number(String(value || '').replace(',', '.'));
    return isFinite(parsed) ? parsed : null;
  }

  canSave(): boolean {
    if (!this.hasValidInput() || this.calculating || !this.calculations.length || this.errorMessage) { return false; }
    return !this.needsPensionCompanySelection() || (!!this.pensionCompanyId && Number(this.pensionCompanyId) !== 3);
  }

  needsPensionCompanySelection(): boolean {
    return this.pensionPercent > 0 && this.pensionMode === 'add' && this.pensionCompanySelectionVisible;
  }

  private updatePensionCompanySelectionVisibility(): void {
    if (this.pensionPercent <= 0 || this.pensionMode !== 'add') {
      this.pensionCompanySelectionVisible = false;
      return;
    }

    this.pensionCompanySelectionVisible = this.calculations
      .some(x => !x.user?.PensionSavingsSettings?.PensionSavingsCompany?.Id ||
        Number(x.user.PensionSavingsSettings.PensionSavingsCompany.Id) === 3);
  }

  save(): void {
    if (!this.canSave()) { return; }
    this.dialogRef.close(this.getResult());
  }

  private getResult(): any {
    const existingPensionCompanyId = this.calculations
      .map(x => x.user?.PensionSavingsSettings?.PensionSavingsCompany?.Id)
      .find(id => !!id && Number(id) !== 3);
    const selectedPensionCompanyId = Number(this.pensionCompanyId) === 3 ? null : this.pensionCompanyId;
    return { calculations: this.calculations, mode: this.mode, salaryType: this.salaryType, amount: this.amount, hours: this.hours, vacationMode: this.vacationMode, pensionPercent: this.pensionPercent, pensionMode: this.pensionMode, pensionCompanyId: selectedPensionCompanyId || existingPensionCompanyId || null, includeCalculationDescription: this.includeCalculationDescription };
  }
}
